import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { projects } from '../../data/constants';
import CardSpotlight from '../CardSpotlight/CardSpotlight';
import styles from './Projects.module.css';

const fadeUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const carouselContainer = {
  hidden: {},
  visible: {}
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const carouselRef = useRef(null);
  const isHoveredRef = useRef(false);
  const isInView = useInView(carouselRef, { once: true, amount: 0.3 });

  useEffect(() => {
    const handleReset = () => setResetKey(prev => prev + 1);
    window.addEventListener('resetProjectsCarousel', handleReset);
    return () => window.removeEventListener('resetProjectsCarousel', handleReset);
  }, []);

  useEffect(() => {
    if (!isInView) return;

    if (resetKey > 0 && carouselRef.current) {
      setTimeout(() => {
        carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      }, 300);
    }

    const scrollInterval = setInterval(() => {
      if (carouselRef.current && !isHoveredRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        // If we've reached the end, scroll back to the beginning
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // Scroll right by approximately one card width (420px + 2rem gap)
          carouselRef.current.scrollBy({ left: 452, behavior: 'smooth' });
        }
      }
    }, 3000); // Wait 3s before moving, giving user time to read the first project

    return () => clearInterval(scrollInterval);
  }, [isInView, resetKey]);

  const openModal = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <section id="projects" className={styles.projectsSection}>
      <div className={styles.container}>
        <motion.div 
          className={styles.header}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUpVariants}
        >
          <span className={styles.label}>PORTFOLIO</span>
          <h2 className={styles.h2}>Featured Projects</h2>
        </motion.div>

        <motion.div 
          ref={carouselRef}
          className={styles.carousel}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={carouselContainer}
          onMouseEnter={() => { isHoveredRef.current = true; }}
          onMouseLeave={() => { isHoveredRef.current = false; }}
        >
          {projects.map((project, index) => (
            <motion.div 
              key={index} 
              className={styles.card}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ y: -8, transition: { duration: 0.2, delay: 0 } }}
            >
              <CardSpotlight style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className={styles.imageContainer} onClick={() => openModal(project)} style={{ cursor: 'pointer' }}>
                  {project.image && (
                    <img 
                      src={Array.isArray(project.image) ? project.image[0] : project.image} 
                      alt={project.title} 
                      className={styles.projectImage} 
                    />
                  )}
                  <div className={styles.imageOverlay}>
                    <span>View Gallery</span>
                  </div>
                </div>
                
                <div className={styles.cardContent}>
                  <div className={styles.cardTop}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {Array.isArray(project.category) ? project.category.map((cat, idx) => (
                        <span key={idx} className={styles.categoryBadge}>{cat}</span>
                      )) : (
                        <span className={styles.categoryBadge}>{project.category}</span>
                      )}
                    </div>
                    <span className={styles.date}>{project.date}</span>
                  </div>
                  <h3 className={styles.title}>{project.title}</h3>
                  <p className={styles.description}>{project.description}</p>
                  
                  <div className={styles.techStack}>
                    {project.tags && project.tags.map((techItem, i) => (
                      <span key={i} className={styles.techPill}>{techItem}</span>
                    ))}
                  </div>

                  <div className={styles.actions}>
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                        GitHub →
                      </a>
                    )}
                    {project.webapp && (
                      <a href={project.webapp} target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                        Live Demo →
                      </a>
                    )}
                    {project.video && (
                      <a href={project.video} target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                        Watch Video →
                      </a>
                    )}
                  </div>
                </div>
              </CardSpotlight>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {selectedProject && (
        <div className={styles.modalBackdrop} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeModal}>&times;</button>
            <h3 className={styles.modalTitle}>{selectedProject.title} - Gallery</h3>
            <div className={styles.modalGallery}>
              {selectedProject.image && [].concat(selectedProject.image).map((mediaSrc, idx) => {
                const isVideo = typeof mediaSrc === 'string' && mediaSrc.match(/\.(mp4|webm|ogg)(\?|$)/i);
                return isVideo ? (
                  <video 
                    key={idx} 
                    src={mediaSrc} 
                    controls 
                    className={styles.modalImg}
                    style={{ outline: 'none', width: '100%' }}
                  />
                ) : (
                  <img 
                    key={idx} 
                    src={mediaSrc} 
                    alt={`${selectedProject.title} ${idx + 1}`} 
                    className={styles.modalImg} 
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
